<template>
  <div class="card-body pt-3 pb-4">
    <template>
      <div class="d-flex justify-content-between">
        <h3 class="mb-0 t-md t-400"><i class="fa al-icon-stars mr-0 pb-1 t-yellow" /> Evaluate this profile</h3>
      </div>
      <!-- List of available templates -->
      <horizontal-list class="mt-2 mb-3">
        <div v-for="(template, index) in templates" :key="index">
          <div class="y-a-label mr-1 mb-1" >
            <link-button
              class="t-sm px-2 d-block"
              :class="index == activeTemplateIndex ? 'bg-blue t-white' : 'bg-white'"
              @click="activateTemplate(index)">
              {{ template.label }}
              <i class="ml-1 pb-2 pl-2 t-sm fa fa-pencil-square-o show-on-hover" @click.stop.prevent="openModalForTemplate(index)" title="Edit configuration" />
              <span class="btn-fade" />
            </link-button>
          </div>
        </div>
      </horizontal-list>

      <!-- The AI response (for the active template) -->
      <div class="d-flex justify-content-between align-items-center">
        <span class="t-sm" style="color: rgba(var(--text-primary), 0.5)">Based on {{ searchFilterObject.label }}'s recent research <span v-if="activeQuery"> about <i>{{ activeQuery }}</i></span> </span>
        <span class="flex-grow-1"><!-- spacer --></span>
        <span v-if="$can('askProfile')" class="t-secondary"><i class="fa fa-info-circle t-sm" v-tooltip="infoCopy()" /></span>
        <animated-button
          icon="fa-clipboard t-sm"
          animatedIcon="fa-check t-green t-sm"
          title="Copy to clipboard"
          @click="onCopyResponse()" />
      </div>
      <spinner v-if="busyStreaming" :containerClass="`width--50px min-height--100px`" />
      <long-text-fixed-height
        v-if="$can('askProfile') && !busyStreaming"
        :truncateAt="400"
        :minHeight="80"
        :fadeColor="`#F5F7F7`"
        class="mt-1 t-400 mb-3">
        <span v-html="response" />
        <div class="d-flex justify-content-end">
          <animated-button
            icon="fa-clipboard t-sm"
            text="Copy"
            animatedText="Copied!"
            animatedIcon="fa-check t-green t-sm"
            @click="onCopyResponse()" />
        </div>
      </long-text-fixed-height>
      <img v-if="!$can('askProfile') && !busyStreaming" :src="require('@/assets/img/ask_profile_locked.svg')" class="mt-1">

      <!-- Template settings modal ; adjusts itself to the selected template-->
      <modal
        :visible="modalOpened"
        :showCloseIcon="false"
        @close="modalOpened = false"
        :extraClasses="`h-100 my-0 modal-dialog-l`">
        <h2 class="mb-3">{{ templates[modalTemplateIndex].label }}<br /></h2>
        <p class="ml-0"><span v-html="renderMarkdown(templates[modalTemplateIndex].description)" /></p>
        <div v-for="(param, index) in templates[modalTemplateIndex].parameters" :key="index">
          <label class="t-500 label mt-1">{{ param.name }}</label>
          <textarea
            :name="'param_'+index"
            @click="onTemplateParameterClick($event, index, param.placeholder)"
            class="form-control mb-3"
            :placeholder="param.placeholder"
            v-model="param.value"
            v-validate="param.required?'required':''"
            data-vv-validate-on="blur"
            :class="{'is-invalid': errors.has('param_'+index)}"
            :rows="paramRows(param.placeholder)"
            />
        </div>
        <block-preview-collapsible
          v-if="$can('askProfile')"
          title="Save my input"
          titleClass="bg-white t-sm"
          :previewHeight="0"
          :openCollapsed="true"
          class="mt-5">
          <checkbox
              class="py-0 px-3 rounded mt-1 bg-white"
              :value="templates[modalTemplateIndex]['remember_preferences']"
              @toggle="toggleRememberPreferences"
            >
            <p class="m-0">
            <span>Save my '{{ templates[modalTemplateIndex].label }}' input</span>
            <br />
            <span class='t-sm' style='color: rgba(var(--text-primary), 0.5)'>To easily repeat the same evaluation on multiple profiles
              <br />- If checked: all of your changes in this window are saved on your computer (until logging out of AcademicLabs)
              <br />- If unchecked: all your changes in this window will be lost when leaving this profile</span>
            </p>
          </checkbox>
          <div class="d-flex align-items-center ml-2 mt-2 mb-2">
            <template v-if="templates[modalTemplateIndex].label !== defaultTemplate">
              <link-button class="px-2 d-block t-sm" icon="fa-check t-sm"
                @click="setDefaultTemplate()">
                Use '{{ templates[modalTemplateIndex].label }}' by default
              </link-button>
              <span class='t-sm' style='color: rgba(var(--text-primary), 0.5)'>('{{ defaultTemplate }}' is now used as the default evaluation when opening a profile.)</span>
            </template>
            <template v-else>
              <i class="fa fa-check t-sm t-green px-2" />
              <span><span class='t-sm t-primary'>'{{ defaultTemplate }}' is set as the default evaluation. It is used automatically when opening a profile.</span><br /><span class='t-sm' style='color: rgba(var(--text-primary), 0.5)'>(To change the default: open your evaluation of choice, and click 'Use by default' under 'Save my input'.)</span></span>
            </template>
          </div>
        </block-preview-collapsible>
        <block-preview-collapsible
          title="Advanced settings"
          titleClass="bg-white t-sm"
          :previewHeight="0"
          :openCollapsed="true"
          class="">
          <p class="mb-0 ml-0 mt-2">
            This evaluation should be based on {{ searchFilterObject.label }}'s recent research about:
          </p>
          <p class="m-2 pt-1">
            <radio-button :name="`basedOnType`"
              :value="BasedOnQuery.CURRENT"
              :valueText="`My current search query (${globalSearchKeyword ? globalSearchKeyword : 'empty'})`"
              :isActive="templates[modalTemplateIndex]['data_based_on'] === BasedOnQuery.CURRENT"
              @input="setBasedOnQueryType"
              class="mb-1"
            />
            <radio-button :name="`basedOnType`"
              :value="BasedOnQuery.MANUAL"
              :isActive="templates[modalTemplateIndex]['data_based_on'] === BasedOnQuery.MANUAL"
              @input="setBasedOnQueryType"
              class="mb-1">
              <input
                type="text"
                @click="setBasedOnQueryType(true, BasedOnQuery.MANUAL)"
                class="form-control p-0 w-75"
                titleClass="bg-white"
                name="manualQuery"
                v-model="templates[modalTemplateIndex]['manual_query']"
                style="height:auto"
                :placeholder="`A research topic of interest (can be any search query; can be left empty)`"
              />
            </radio-button>
            <radio-button :name="`basedOnType`"
              :value="BasedOnQuery.AI"
              :valueText="`Let the AI decide`"
              :isActive="templates[modalTemplateIndex]['data_based_on'] === BasedOnQuery.AI"
              @input="setBasedOnQueryType"
              class="mb-1"
            />
          </p>
          <p class="t-sm w-25"
            v-tooltip.bottom-start="{ content: howThisWorksCopy(), autoHide: false, classes: ['tooltip-left-text','tooltip-no-arrow', 'tooltip-wide']}">
            <i class="fa fa-info-circle t-secondary"></i> How does this work?
          </p>
        </block-preview-collapsible>
        <div class="d-flex flex-row justify-content-end align-items-center mt-3">
          <span v-if="!isShowingDefaultTemplate()">
            Each '{{ templates[modalTemplateIndex].label }}' evaluation consumes 10 AI credits.
          </span>
          <span v-else>
            Producing summaries does not consume any AI credits.
          </span>
          <span class="flex-grow-1"><!-- spacer --></span>
          <button
            class="btn bg-transparent no-border t-primary"
            type="button"
            @click="modalOpened = false">Close
          </button>
          <button
            class="btn btn-primary no-border ml-3"
            type="button"
            @click="updateResponseFromModal"
            :disabled="!$can('askProfile')">
            Evaluate
            <i class="fa fa-lock semi-transparent ml-2" v-if="!$can('askProfile')" />
          </button>
        </div>
      </modal>
    </template>
  </div>
</template>

<script>
/**
 * This component is shown on all profile pages.
 * It produces various AI responses based on the profile's most recent documents.
 * The user doesn't directly write the prompt, but can choose between several different templates.
 */
import Spinner from '@/components/shared/Spinner'
import LongTextFixedHeight from '@/components/shared/LongTextFixedHeight'
import HorizontalList from '@/components/shared/HorizontalListExpandable'
import LinkButton from '@/components/shared/form/LinkButton'
import Modal from '@/components/shared/ModalStandAlone'
import RadioButton from '@/components/shared/form/RadioButton'
import BlockPreviewCollapsible from '@/components/shared/BlockPreviewCollapsible'
import AnimatedButton from '@/components/shared/AnimatedButton'
import Checkbox from '@/components/shared/form/Checkbox2'
import SearchService from '@/services/search'
import markdown from '@/plugins/markdown'
import searchHelpers from '@/mixins/search/helpers'

// Enum for the "Based on recent research about" radio buttons
const BasedOnQuery = Object.freeze({
  CURRENT: 'current',
  AI: 'AI',
  MANUAL: 'manual'
})

export default {
  name: 'AskThisProfile',
  mixins: [searchHelpers],
  components: {
    Spinner,
    LongTextFixedHeight,
    LinkButton,
    AnimatedButton,
    Modal,
    HorizontalList,
    RadioButton,
    BlockPreviewCollapsible,
    Checkbox
  },
  props: {
    // Either the most specific organisation type (e.g. 'Spinoff'), 'Researcher' or 'Research Group'
    profileType: {
      type: String,
      required: true
    },
    // Which ES indexes should we check to get "the most recent research" for this profile
    searchIndices: {
      type: Array,
      required: true
    },
    // Needed to build the ES query that gets "the most recent research" of this profile
    searchFilterObject: {
      type: Object,
      required: true
    },
    // Any additional data about the profile (about-text)
    extraData: {
      type: Object,
      required: false
    }
  },
  data () {
    return {
      activeTemplateIndex: 0, // Which template is currently active
      defaultTemplate: 'Summary', // Which template to automatically run on opening a profile
      modalTemplateIndex: 0, // The modal will show the configuration of this template
      response: '', // The AI's response (in Markdown)
      modalOpened: false, // Show/hide the Customize modal
      templates: [{ 'parameters': [] }], // All available templates (default value is a minimal placeholder)
      abortController: null, // So we can abort the stream, if needed
      activeQuery: '', // Which 'based on' query was used to produce the current response
      mdConverter: undefined, // Markdown converter (showdown)

      // Declare any "enums" here, so Vue can see them
      BasedOnQuery
    }
  },
  created () {
    this.mdConverter = markdown.getMarkdownConverter()
    this.loadTemplates()
  },
  computed: {
    busyStreaming () {
      return !this.templates || (this.isEmpty(this.response) && this.abortController != null)
    }
  },
  methods: {
    toggleRememberPreferences () {
      this.templates[this.modalTemplateIndex]['remember_preferences'] = !this.templates[this.modalTemplateIndex]['remember_preferences']
      this.writePreferences()
    },
    setDefaultTemplate () {
      this.defaultTemplate = this.templates[this.modalTemplateIndex].label
      this.writePreferences()
    },
    async updateResponseFromModal () {
      // Double-check whether all fields are filled in
      await this.$validator.validateAll()
      if (this.errors.any()) {
        return
      }

      this.modalOpened = false
      this.$nextTick(() => {
        this.response = '' // Activates the spinner animation
        // Without the nextTick: the spinner starting / response disappearing would interfere with the modal closing at the same time
        // => The modal won't properly close and won't re-enable the page's scrollbar => The user cannot scroll the page anymore! => Good times!
      })
      this.activeTemplateIndex = this.modalTemplateIndex
      this.writePreferences()
      this.streamResponse()
    },
    async streamResponse () {
      // If we're already busy streaming, abort that stream first
      if (this.abortController) {
        this.abortController.abort()
      }
      this.abortController = new AbortController()

      const filterSearchObject = this.getSearchObjectPreview(this.searchFilterObject, this.searchIndices)

      var streamRequestData = {
        'profile_slug': this.$route.params.id,
        'profile_type': this.profileType,
        'search_filter_object': filterSearchObject,
        'search_query': this.globalSearchKeyword,
        'extra_data': this.extraData,
        'template': this.templates[this.activeTemplateIndex]
      }

      var searchQuery = this.globalSearchKeyword
      const dataBasedOn = this.templates[this.activeTemplateIndex]['data_based_on']
      if (dataBasedOn === BasedOnQuery.MANUAL) {
        searchQuery = this.templates[this.activeTemplateIndex]['manual_query']
      } else if (dataBasedOn === BasedOnQuery.AI) {
        const recommendation = await SearchService.getAskProfileQuery(streamRequestData)
        searchQuery = recommendation['query']
        filterSearchObject['indexes'] = recommendation['indexes']
      }

      streamRequestData['search_query'] = searchQuery
      streamRequestData['search_filter_object'] = filterSearchObject
      this.activeQuery = searchQuery

      // Receive the LLM response in chunks
      SearchService.getAskProfileStream(streamRequestData, this.$ls.getToken(), this.abortController.signal).then(response => {
        this.response = ''
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let result = ''

        const read = () => {
          return reader.read().then(({ done, value }) => {
            if (done) {
              this.abortController = null
              return
            }
            const chunk = decoder.decode(value, { stream: true })
            result += chunk

            this.response = this.renderMarkdown(result)
            return read()
          })
        }

        return read()
      }).catch(error => {
        if (error.name === 'AbortError') {
          // The user just aborted this stream and started a new one; that's fine!
        } else {
          this.response = '<i>An error occurred while retrieving the AI\'s response</i>'
        }
      })
    },
    renderMarkdown (mdText) {
      return markdown.renderMarkdown(this.mdConverter, mdText)
    },
    setBasedOnQueryType (checked, value) {
      this.templates[this.modalTemplateIndex]['data_based_on'] = value
      this.writePreferences()
    },
    loadTemplates () {
      // Get all available templates
      // and prefill them with any saved user preferences

      const prefs = this.$ls.getAskThisProfilePreferences()

      const requestData = {
        'profile_type': this.profileType,
        'profile_name': this.searchFilterObject.label
      }
      SearchService.getAskProfileTemplates(requestData).then(response => {
        this.templates = response['templates']
        this.templates.forEach((template, index) => {
          var loadedFromPrefs = false
          if (template.label in prefs) {
            // If the user has saved preferences for this template
            try {
              const templPrefs = prefs[template.label]
              template['remember_preferences'] = true
              template.parameters.forEach((param, index) => {
                param.value = templPrefs.parameters[index]
              })
              template['data_based_on'] = templPrefs['data_based_on']
              template['manual_query'] = templPrefs['manual_query']
              loadedFromPrefs = true
            } catch (err) {}
          }

          if (!loadedFromPrefs) {
            // Alternatively, fall back to defaults
            template['remember_preferences'] = false
            template['manual_query'] = ''
            template.parameters.forEach((param, index) => {
              param.value = ''
            })
          }
        })

        // Switch to the default template
        try {
          if ('default_template' in prefs) {
            this.defaultTemplate = prefs['default_template']
            this.activeTemplateIndex = this.templates.findIndex(t => t.label === this.defaultTemplate)
            if (this.activeTemplateIndex === -1) {
              // Can happen if the user has set a researcher-only template as default, and then visits a company profile
              this.activeTemplateIndex = 0
            }
          }
        } catch (err) {}

        // Run this template now
        if (this.$can('askProfile')) {
          if (this.templateNeedsInput(this.activeTemplateIndex)) {
            // We cannot autorun a template if it needs more input
            this.response = `<i class="t-sm fa fa-exclamation-triangle"></i> You've configured '${this.defaultTemplate}' as your default, but this evaluation cannot run automatically without your input. <b>To resolve this</b>: click '${this.defaultTemplate}', fill in the required questions, and check the box under 'Save my input'.`
          } else {
            this.streamResponse()
          }
        }
      })
    },
    writePreferences () {
      // Writes any user preferences to localstorage
      var prefs = {}
      prefs['default_template'] = this.defaultTemplate
      this.templates.forEach((template, index) => {
        if (template['remember_preferences']) {
          var templPrefs = {}
          templPrefs['data_based_on'] = template['data_based_on']
          templPrefs['manual_query'] = template['manual_query']

          templPrefs['parameters'] = template.parameters.map(param => param.value)
          prefs[template.label] = templPrefs
        }
      })
      this.$ls.setAskThisProfilePreferences(prefs)
    },
    activateTemplate (index) {
      if (this.templateNeedsInput(index)) {
        this.openModalForTemplate(index)
      } else {
        if (index === this.activeTemplateIndex) {
          return // No need to do anything if the we're still on the same template
        }
        this.response = ''
        this.activeTemplateIndex = index
        this.streamResponse()
      }
    },
    openModalForTemplate (index) {
      this.modalTemplateIndex = index
      this.modalOpened = true
    },
    templateNeedsInput (index) {
      // Does this template have any unfilled, required parameters?
      const template = this.templates[index]
      return template.parameters.some(param => param.value === '' && param.required)
    },
    templateHasParameters (index) {
      const template = this.templates[index]
      return template.parameters.length > 0
    },
    onCopyResponse () {
      markdown.copyMdToClipboard(this.mdConverter, this.response)
    },
    paramRows (placeholder) {
      // Estimate how many rows a textarea needs, to fully show the placeholder text
      return placeholder.split('\n').length + 1
    },
    onTemplateParameterClick (event, paramIndex, placeholder) {
      // Fill in the placeholder text, if the textarea is empty
      const el = event.target
      if (this.isEmpty(this.templates[this.modalTemplateIndex].parameters[paramIndex].value)) {
        this.$set(
          this.templates[this.modalTemplateIndex].parameters[paramIndex],
          'value',
          placeholder
        )
        // Also force the value to be shown immediately, by directly setting it in the DOM
        // (otherwise some reactivity and/or v-validate quirks, where the text only shows once you leave focus)
        el.value = placeholder

        this.$nextTick(() => {
          el.select()
        })
      }
    },
    isShowingDefaultTemplate () {
      // Summary is the default template (because it doesn't cost any credits)
      return this.templates[this.modalTemplateIndex].label === 'Summary'
    },
    infoCopy () {
      return `'<b>Summary' evaluations can be used free of charge.<br />All other evaluations consume 10 AI credits per use.</b><br /><br />
      Your use of this AI-enabled feature is private, anonymous and will not be used to train any AI models. Like all generative AI, our AI can provide inaccurate responses. We recommend verifying these before relying on them.`
    },
    howThisWorksCopy () {
      return `Our AI first searches for a relevant selection of recent documents in this ${this.profileType} profile. The evaluation is then done based on those documents.

      <br /><br /><b>With these controls, you can specify how this document selection is made</b>: either by searching for your current query${this.globalSearchKeyword ? ` (${this.globalSearchKeyword})` : ``}, a query of your choice, or the AI can choose the query by itself. (Tip: you can use the usual search operators: AND, OR, ... )
      
      <br /><br />How many documents are selected? - Up to ${this.searchIndices.length * 10}. They are chosen from the following categories: ${this.joinWithAnd(this.searchIndices.map(index => this.getIndexAsString(index)))}`
    },
    joinWithAnd (arr) {
      // Turns ["apple", "pear", "banana", "pineapple"]
      // into "apple, pear, banana and pineapple"
      if (arr.length === 0) return ''
      if (arr.length === 1) return arr[0]
      if (arr.length === 2) return arr[0] + ' and ' + arr[1]
      return arr.slice(0, -1).join(', ') + ' and ' + arr[arr.length - 1]
    }

  }
}
</script>
<style lang="scss" scoped>
@import "@/assets/scss/_loaders/_colors.scss";

.show-on-hover {
  display: none;
  padding: 0;
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
  z-index: 2;
}

.link-button:hover .show-on-hover {
  display: block;
  color: $text-primary;

}

.show-on-hover:hover {
  color: $blue !important;
}

.btn-fade {
  display: none;
  position: absolute;
  border-radius: 4px;
  top: 0;
  right: 0;
  width: 44px;
  height: 100%;
  background: linear-gradient(to right, transparent, #ffffff 80%);
  z-index: 1;
  pointer-events: none;
}

.link-button:hover .btn-fade {
  display: block;
}

.copy-button:active {
  color: rgba(var(--text-primary), 0.75);
}
</style>
